import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const QuickSessionCreation = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    service: '',
    date: '',
    time: '',
    duration: '2',
    guests: 1,
    specialRequests: '',
    wellnessGoals: [],
    practitioner: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    { id: 'single-ees', name: 'Single EES Session', duration: '2 hours', price: 150 },
    { id: 'day-pass', name: 'Day Pass', duration: 'Full day', price: 300 },
    { id: 'weekly-package', name: 'Weekly Package', duration: '7 days', price: 1200 },
    { id: 'meditation', name: 'Guided Meditation', duration: '1 hour', price: 75 },
    { id: 'sound-healing', name: 'Sound Healing', duration: '90 minutes', price: 120 },
    { id: 'energy-work', name: 'Energy Healing Session', duration: '2 hours', price: 180 }
  ];

  const practitioners = [
    { id: 'dr-maya', name: 'Dr. Maya Chen', specialties: ['EES', 'Energy Healing'] },
    { id: 'sarah-wellness', name: 'Sarah Wilson', specialties: ['Meditation', 'Sound Healing'] },
    { id: 'michael-energy', name: 'Michael Torres', specialties: ['EES', 'Wellness Coaching'] }
  ];

  const wellnessGoalOptions = [
    'Stress Relief',
    'Energy Boost',
    'Better Sleep',
    'Pain Relief',
    'Mental Clarity',
    'Detoxification',
    'Emotional Balance',
    'Spiritual Growth'
  ];

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'wellnessGoals') {
      setFormData(prev => ({
        ...prev,
        wellnessGoals: checked
          ? [...prev.wellnessGoals, value]
          : prev.wellnessGoals.filter(goal => goal !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    const selectedService = services.find(s => s.id === formData.service);
    const selectedPractitioner = practitioners.find(p => p.id === formData.practitioner);

    const sessionData = {
      ...formData,
      serviceDetails: selectedService,
      practitionerDetails: selectedPractitioner,
      totalPrice: selectedService ? selectedService.price * formData.guests : 0,
      createdAt: new Date().toISOString()
    };

    console.log('New session created:', sessionData);
    onSubmit?.(sessionData);
    setIsSubmitting(false);
  };

  const selectedService = services.find(s => s.id === formData.service);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Create New EES Session</h2>
            <p className="text-sm text-text-secondary mt-1">Schedule a wellness session for your guest</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Guest Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Guest Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Guest Name *
                </label>
                <input
                  type="text"
                  name="guestName"
                  value={formData.guestName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                  placeholder="Enter guest full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="guestEmail"
                  value={formData.guestEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                  placeholder="guest@email.com"
                />
              </div>
            </div>
          </div>

          {/* Service Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Service Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Service Type *
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              >
                <option value="">Select a service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - {service.duration} - ${service.price}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Assigned Practitioner *
              </label>
              <select
                name="practitioner"
                value={formData.practitioner}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              >
                <option value="">Select a practitioner</option>
                {practitioners.map(practitioner => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {practitioner.name} - {practitioner.specialties.join(', ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Schedule</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Time *
                </label>
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <option value="">Select time</option>
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Number of Guests
              </label>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, guests: Math.max(1, prev.guests - 1) }))}
                  className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center hover:bg-secondary-200 transition-colors duration-200"
                  disabled={formData.guests <= 1}
                >
                  <Icon name="Minus" size={16} />
                </button>
                <span className="text-lg font-medium text-text-primary min-w-[2rem] text-center">
                  {formData.guests}
                </span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, guests: Math.min(12, prev.guests + 1) }))}
                  className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center hover:bg-secondary-200 transition-colors duration-200"
                  disabled={formData.guests >= 12}
                >
                  <Icon name="Plus" size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Wellness Goals */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Wellness Goals (Optional)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {wellnessGoalOptions.map(goal => (
                <label key={goal} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    name="wellnessGoals"
                    value={goal}
                    checked={formData.wellnessGoals.includes(goal)}
                    onChange={handleInputChange}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-text-secondary">{goal}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Special Requests */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Special Requests or Notes
            </label>
            <textarea
              name="specialRequests"
              value={formData.specialRequests}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200 resize-none"
              placeholder="Any special requirements, dietary restrictions, or wellness preferences..."
            />
          </div>

          {/* Session Summary */}
          {selectedService && formData.date && formData.time && (
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
              <h4 className="font-medium text-primary mb-2">Session Summary</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-primary-700">Service:</span>
                  <span className="text-primary">{selectedService.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-700">Duration:</span>
                  <span className="text-primary">{selectedService.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-700">Date & Time:</span>
                  <span className="text-primary">
                    {new Date(formData.date).toLocaleDateString()} at {formData.time}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-700">Guests:</span>
                  <span className="text-primary">{formData.guests}</span>
                </div>
                <div className="flex justify-between font-medium text-primary pt-2 border-t border-primary-200">
                  <span>Total Price:</span>
                  <span>${selectedService.price * formData.guests}</span>
                </div>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:bg-primary/50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSubmitting && <Icon name="Loader" size={16} className="animate-spin" />}
              <span>{isSubmitting ? 'Creating Session...' : 'Create Session'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickSessionCreation;